// Composables
import { createRouter, createWebHistory } from "vue-router";
import routes from "./routes";
import routesAuth from "./routesAuth";
import store from "@/store/store";
import { useToast } from "vue-toastification";
import axios from "axios";
import useCookies from "@/utils/cookies";
import routesMobile from "@/router/routesMobile";
import routesVisitor from "@/router/routesVisitor";
import setUserData from "@/utils/setUserData";
import { computed } from "vue";
import { eventBus } from "@/utils/eventBus";

const cookies = useCookies();

const isLoggedIn = computed(() => store.state.auth.isLoggedIn);
const isTemporary = computed(() => store.state.auth.user.isTemporary);
const isEmailVerified = computed(() => store.state.auth.user.isEmailVerified);

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes: [...routes, ...routesAuth, ...routesMobile, ...routesVisitor],
});

const isAuthRoute = (pathName) => {
  return routesAuth.some((route) => route.name === pathName);
};

router.beforeEach(async (to, from, next) => {
  console.log("🚀 Router beforeEach - Navigation to:", to.name, to.path);

  // Global payment cancellation detection
  // Check if user returned from cancelled payment and handle it globally for ALL routes
  if (to.query.payment_cancelled === 'true') {
    // Remove the parameter from URL to clean it up
    const newQuery = { ...to.query };
    delete newQuery.payment_cancelled;

    // Redirect to the same route without the parameter
    const cleanRoute = {
      ...to,
      query: newQuery
    };

    // Add a small delay to ensure components are mounted before emitting event
    setTimeout(() => {
      eventBus.emit('open-plans-modal');
    }, 500);

    // Navigate to the clean route
    next(cleanRoute);
    return;
  }

  console.log("📊 Auth state:", {
    hasToken: !!cookies.get("apiToken"),
    isLoggedIn: isLoggedIn.value,
    isTemporary: isTemporary.value,
    isEmailVerified: isEmailVerified.value,
    isAuthRoute: isAuthRoute(to.name)
  });

  const requiresAuthorization = to.matched.some((record) => record.meta.requiresAuthorization);

  // If no token exists, redirect to login (not creating temporary user automatically)
  if (!cookies.get("apiToken") && !isAuthRoute(to.name)) {
    console.log("🔐 No token found, redirecting to login | From:", from.path, "| To:", to.path);
    next({
      path: "/login",
      query: { redirect: to.fullPath },
    });
    return;
  }

  if (isLoggedIn.value || cookies.get("apiToken")) {
    console.log("📝 Setting user data...");
    await setUserData();
  }

  if (requiresAuthorization && isTemporary.value && !isAuthRoute(to.name)) {
    console.log("⚠️ Temporary user accessing protected route, redirecting to login");
    useToast().error("You must log in to view this page.");
    next({
      path: "/login",
      query: { redirect: to.fullPath },
    });
    return;
  }

  if (isLoggedIn.value && !isTemporary.value && !isEmailVerified.value && !isAuthRoute(to.name)) {
    console.log("✉️ Email not verified, redirecting to verification");
    next({
      path: "/verification-required",
    });
    return;
  }

  // Prevent normal logged-in users from accessing auth routes
  if (isLoggedIn.value && !isTemporary.value && isEmailVerified.value && isAuthRoute(to.name)) {
    console.log("🏠 Logged in user accessing auth route, redirecting to landing page");
    next({ path: "/" });
    return;
  }

  console.log("✅ Navigation allowed, proceeding to:", to.path);
  next();
});

const createTemporaryUser = async () => {
  try {
    const response = await axios.post("/api/temporary-user");
    store.commit("auth/login", response.data.token);
    cookies.set("apiToken", response.data.token, 7);
  } catch (error) {
    console.error("Error creating temporary user:", error);
  }
};

export default router;
